import { Link, NavLink, useNavigate } from "react-router-dom";
import { useState } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { useProjects } from "@/contexts/ProjectContext";
import Logo from "@/components/brand/Logo";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuLabel,
} from "@/components/ui/dropdown-menu";
import {
  Sheet, SheetContent, SheetTrigger, SheetHeader, SheetTitle,
} from "@/components/ui/sheet";
import {
  LayoutDashboard, Search, FileText, Calendar, ListChecks, Sparkles, ShieldCheck,
  TrendingUp, Link2, MessageSquareQuote, Plug, Settings, ScrollText, Users, LogOut, ChevronDown, Menu,
} from "lucide-react";

const NAV_GROUPS = [
  {
    label: "Growth",
    items: [
      { to: "/dashboard", icon: LayoutDashboard, label: "Dashboard" },
      { to: "/sales-growth", icon: TrendingUp, label: "Sales Growth" },
      { to: "/growth-plan", icon: Sparkles, label: "Growth Plan" },
    ],
  },
  {
    label: "Content",
    items: [
      { to: "/keywords", icon: Search, label: "Keywords" },
      { to: "/articles", icon: FileText, label: "Article Generator" },
      { to: "/content-calendar", icon: Calendar, label: "Content Calendar" },
      { to: "/conversion-blocks", icon: MessageSquareQuote, label: "Conversion Blocks" },
    ],
  },
  {
    label: "SEO & Authority",
    items: [
      { to: "/audit", icon: ShieldCheck, label: "Technical Audit" },
      { to: "/ai-visibility", icon: Sparkles, label: "AI Visibility" },
      { to: "/backlinks", icon: Link2, label: "Authority" },
      { to: "/internal-links", icon: Link2, label: "Internal Links" },
    ],
  },
  {
    label: "Operations",
    items: [
      { to: "/usage", icon: TrendingUp, label: "Usage & Credits" },
      { to: "/integrations", icon: Plug, label: "Integrations" },
      { to: "/reports", icon: ScrollText, label: "Reports" },
      { to: "/settings", icon: Settings, label: "Settings" },
      { to: "/tasks", icon: ListChecks, label: "Tasks" },
    ],
  },
];

function SidebarNav({ user, onClickItem }) {
  return (
    <nav className="flex-1 overflow-y-auto px-3 py-4 space-y-5">
      {NAV_GROUPS.map((group) => (
        <div key={group.label}>
          <p className="px-2 editorial-overline text-[10px] mb-2">{group.label}</p>
          <div className="space-y-0.5">
            {group.items.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                onClick={onClickItem}
                data-testid={`nav-${item.label.toLowerCase().replace(/\s+/g, "-")}`}
                className={({ isActive }) =>
                  `flex items-center gap-2.5 px-2.5 py-2 text-sm rounded-sm transition-colors ${
                    isActive
                      ? "bg-navy-900 text-ivory-50"
                      : "text-navy-900/70 hover:bg-ivory-100 hover:text-navy-900"
                  }`
                }
              >
                <item.icon size={15} strokeWidth={1.6} />
                <span>{item.label}</span>
              </NavLink>
            ))}
          </div>
        </div>
      ))}
      {user?.role === "admin" && (
        <div>
          <p className="px-2 editorial-overline text-[10px] mb-2">Admin</p>
          <NavLink
            to="/admin"
            onClick={onClickItem}
            data-testid="nav-admin"
            className={({ isActive }) =>
              `flex items-center gap-2.5 px-2.5 py-2 text-sm rounded-sm ${
                isActive ? "bg-navy-900 text-ivory-50" : "text-navy-900/70 hover:bg-ivory-100"
              }`
            }
          >
            <Users size={15} strokeWidth={1.6} />
            <span>Admin Panel</span>
          </NavLink>
        </div>
      )}
    </nav>
  );
}

export default function DashboardLayout({ children }) {
  const { user, logout } = useAuth();
  const { projects, activeProjectId, selectProject } = useProjects();
  const navigate = useNavigate();
  const [mobileOpen, setMobileOpen] = useState(false);

  return (
    <div className="min-h-screen bg-ivory-50 text-navy-900 flex" data-testid="dashboard-layout">
      {/* Desktop sidebar */}
      <aside className="hidden lg:flex flex-col w-64 border-r border-border bg-white sticky top-0 h-screen">
        <div className="px-5 py-5 border-b border-border">
          <Link to="/dashboard"><Logo variant="dark" /></Link>
        </div>
        <SidebarNav user={user} />
      </aside>

      {/* Main */}
      <div className="flex-1 flex flex-col min-w-0">
        <header className="bg-white border-b border-border h-14 px-3 sm:px-4 lg:px-8 flex items-center justify-between sticky top-0 z-20">
          <div className="flex items-center gap-2 sm:gap-3">
            {/* Mobile menu */}
            <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
              <SheetTrigger asChild>
                <button className="lg:hidden p-1.5 hover:bg-ivory-100 rounded-sm" data-testid="mobile-menu-btn">
                  <Menu size={18} />
                </button>
              </SheetTrigger>
              <SheetContent side="left" className="w-72 p-0 bg-white">
                <SheetHeader className="sr-only">
                  <SheetTitle>Navigation</SheetTitle>
                </SheetHeader>
                <div className="px-5 py-5 border-b border-border">
                  <Link to="/dashboard" onClick={() => setMobileOpen(false)}><Logo variant="dark" /></Link>
                </div>
                <SidebarNav user={user} onClickItem={() => setMobileOpen(false)} />
              </SheetContent>
            </Sheet>

            <Link to="/dashboard" className="lg:hidden"><Logo variant="dark" withWordmark={false} /></Link>
            {projects.length > 0 && (
              <Select value={activeProjectId || ""} onValueChange={selectProject}>
                <SelectTrigger className="w-44 sm:w-56 h-9 rounded-sm bg-ivory-100 border-border" data-testid="project-selector">
                  <SelectValue placeholder="Select project" />
                </SelectTrigger>
                <SelectContent>
                  {projects.map((p) => (
                    <SelectItem key={p.id} value={p.id} data-testid={`project-option-${p.id}`}>
                      {p.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
          </div>
          <div className="flex items-center gap-2 sm:gap-3">
            <span className="hidden md:inline text-xs editorial-overline">Internal Growth Engine</span>
            <DropdownMenu>
              <DropdownMenuTrigger className="flex items-center gap-2 px-2.5 py-1.5 rounded-sm hover:bg-ivory-100" data-testid="user-menu-trigger">
                <span className="w-7 h-7 rounded-full bg-navy-900 text-ivory-50 flex items-center justify-center text-[11px] font-semibold">
                  {user?.name?.[0] || "U"}
                </span>
                <span className="hidden md:inline text-sm text-navy-900">{user?.name}</span>
                <ChevronDown size={14} />
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="rounded-sm">
                <DropdownMenuLabel className="font-serif">{user?.email}</DropdownMenuLabel>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={() => navigate("/settings")} data-testid="user-menu-settings">Settings</DropdownMenuItem>
                {user?.role === "admin" && (
                  <DropdownMenuItem onClick={() => navigate("/admin")} data-testid="user-menu-admin">Admin Panel</DropdownMenuItem>
                )}
                <DropdownMenuSeparator />
                <DropdownMenuItem
                  onClick={() => { logout(); navigate("/"); }}
                  data-testid="user-menu-logout"
                >
                  <LogOut size={14} className="mr-2" /> Sign out
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </header>
        <main className="flex-1 px-3 sm:px-6 lg:px-10 py-6 sm:py-8 max-w-[1400px] w-full mx-auto" data-testid="dashboard-main">
          {children}
        </main>
      </div>
    </div>
  );
}
